import type { FC } from 'react';
import React, { useCallback } from 'react';
import type { MenuSectionDto } from '../../../../generated-api';
import { useSearch } from '../../../context/SearchContext';
import classNames from 'clsx';
import { SearchBar } from '@ordr-as/umami-react/search-bar';
import { useLocation } from '../../../context/LocationContext';
import MenuSectionButton from './MenuSectionButton';
import MenuAccordion from './MenuAccordion';

interface Props {
  menuSections: MenuSectionDto[];
  selectedSectionId?: string;
  onSectionSelected: (sectionId: string) => void;
}

const MenuSectionsSidebar: FC<Props> = ({ menuSections, onSectionSelected, selectedSectionId }) => {
  const {
    searchText,
    setSearchText,
    shouldExpandSearchBar,
    setShouldExpandSearchBar,
    handleSearchSubmit,
    searchError,
    setSearchError,
  } = useSearch();
  const { location } = useLocation();

  const handleSectionClick = useCallback(
    (menuSection: MenuSectionDto) => {
      onSectionSelected(menuSection.sectionId);
    },
    [onSectionSelected]
  );

  const handleSearchTextChange = useCallback(
    (value: string) => {
      setSearchText(value);
      // Clear error when user starts typing
      if (searchError) {
        setSearchError(undefined);
      }
    },
    [setSearchText, searchError, setSearchError]
  );

  const asideClassname = classNames('bg-brand-white h-main overflow-scroll pb-9', {
    'tablet:col-span-2': !shouldExpandSearchBar,
    'tablet:col-span-3': shouldExpandSearchBar,
  });

  return (
    <aside className={asideClassname}>
      <div className="flex flex-col m-2">
        <SearchBar
          setActive={setShouldExpandSearchBar}
          fullWidth
          value={searchText}
          setValue={handleSearchTextChange}
          onSubmit={handleSearchSubmit}
          aria-label="Search for product"
        />
        {searchError && <div className="mt-2 text-sm text-orange-darkest px-2">{searchError}</div>}
      </div>
      {location.isFoodCourt ? (
        <MenuAccordion
          handleSectionClick={handleSectionClick}
          sections={menuSections}
          selectedSectionId={selectedSectionId}
        />
      ) : (
        <>
          {menuSections.map((menuSection) => (
            <MenuSectionButton
              key={menuSection.sectionId}
              active={menuSection.sectionId === selectedSectionId}
              menuSection={menuSection}
              handleSectionClick={handleSectionClick}
            />
          ))}
        </>
      )}
    </aside>
  );
};
export default MenuSectionsSidebar;
